import { Product } from "../../common/models/product";
import { productInterestProps } from "../../visitorTracking";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  cheapestTierTotal,
  convertPriceValueAndUnit,
  formatPriceValueString,
} from "./price-helpers";
import {
  AddressFormat,
  classNames,
  formatAddress,
  saveInputChangeInHookState,
} from "../../common/functions";
import { UnitSelect } from "./InventoryDetailsPrice";
import AddressAutocomplete from "../../common/components/AddressAutocomplete";
import { Address } from "../../common/models/company";
import { CartItem, parseStoredCart } from "../../common/models/cart";
import { persistShoppingCart } from "../../cart/serverCart";
import {
  LAST_USED_ADDRESS_KEY,
  LAST_USED_GUEST_EMAIL_KEY,
  LAST_USED_GUEST_PHONE_KEY,
} from "../../common/localStorage";
import { useNotifications, useUser } from "../../common/stateDefs";
import { useDimensionsUnit } from "../../buyerStateDefs";
import {
  getAvailableQuantityUnits,
  getQuantityError,
  resolveQuantityUnit,
  simpleFormatUnit,
} from "./quantity-helpers";
import { useLocation, useNavigate } from "react-router-dom";
import { usePostHog } from "posthog-js/react";
import { captureAnalyticsEvent, identifyAnalyticsUser } from "../../analytics";

type AddToCartFormProps = {
  product: Product;
  targetUnit?: string; // Optional target unit for the price
};

export const AddToCartForm = ({ product, targetUnit }: AddToCartFormProps) => {
  const user = useUser((state) => state.user);
  const [open, setOpen] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [millCertRequired, setMillCertRequired] = useState(false);
  const [quantityRequired, setQuantityRequired] = useState("");
  const [quantityUnit, setQuantityUnit] = useState(targetUnit);
  const [deliveryAddress, setDeliveryAddress] = useState<Address | null>(null);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestPhone, setGuestPhone] = useState("");
  const [hasCachedGuestEmail, setHasCachedGuestEmail] = useState(false);
  const [showAddressInput, setShowAddressInput] = useState(true);
  const [notes, setNotes] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const pingShoppingCartUpdateNotification = useNotifications(
    (state) => state.incrementShoppingCartUpdateNotificationCount,
  );
  const qtyInputRef = useRef<HTMLInputElement>(null);
  const posthog = usePostHog();

  const dimensionUnit = useDimensionsUnit((state) => state.dimensionsUnit);

  const targetPriceUnit = ["ft", "in"].includes(dimensionUnit) ? "lb" : "kg";
  const initialPriceUnit =
    convertPriceValueAndUnit(product, targetPriceUnit)?.unit ||
    product.price?.unit;

  const computedPrice =
    !!quantityUnit &&
    !!product.price?.value &&
    convertPriceValueAndUnit(product, quantityUnit);

  const isAddressProvided = (
    address: Address | null | undefined,
  ): address is Address =>
    !!address &&
    (address.streetLines.some((line) => line.trim().length > 0) ||
      address.city.trim().length > 0 ||
      address.postalCode.trim().length > 0 ||
      !!address.stateOrProvinceCode?.trim());

  const handleAddressSelected = (address: Address) => {
    setDeliveryAddress(address);
    setShowAddressInput(false);
    setError(null);
    // save the address to local storage as the last used address
    localStorage.setItem(LAST_USED_ADDRESS_KEY, JSON.stringify(address));
  };

  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const q = searchParams.get("q") || "";
    console.log("Search", q);
    setSearchQuery(q);
  }, [location.search]);

  const quantityError = getQuantityError({
    product,
    quantityRequired,
    quantityUnit,
  });

  const availableUnits = useMemo(
    () =>
      getAvailableQuantityUnits({
        product,
        preferredUnit: initialPriceUnit,
      }),
    [initialPriceUnit, product],
  );

  useEffect(() => {
    const lastUsedAddress = localStorage.getItem(LAST_USED_ADDRESS_KEY);
    const companyAddress = user?.company?.address;
    if (lastUsedAddress) {
      try {
        const addr = JSON.parse(lastUsedAddress) as Address;
        if (isAddressProvided(addr)) {
          setDeliveryAddress(addr);
          setShowAddressInput(false);
          return;
        }
      } catch (e) {
        // ignore
      }
    }
    // if there's no last used address, but the user has a company address, use that
    if (isAddressProvided(companyAddress) && !deliveryAddress) {
      setDeliveryAddress(companyAddress);
      setShowAddressInput(false);
    }
  }, [user]);

  useEffect(() => {
    if (user?.email) {
      setGuestEmail(user.email);
      setHasCachedGuestEmail(true);
      return;
    }

    const cachedGuestEmail =
      localStorage.getItem(LAST_USED_GUEST_EMAIL_KEY) || "";
    setGuestEmail(cachedGuestEmail);
    setHasCachedGuestEmail(!!cachedGuestEmail.trim());
  }, [user]);

  useEffect(() => {
    setGuestPhone(localStorage.getItem(LAST_USED_GUEST_PHONE_KEY) || "");
  }, []);

  useEffect(() => {
    if (availableUnits.length === 0) {
      if (quantityUnit !== undefined) {
        setQuantityUnit(undefined);
      }
      return;
    }

    const resolved = resolveQuantityUnit(quantityUnit, availableUnits);
    if (resolved !== quantityUnit) {
      setQuantityUnit(resolved);
    }
  }, [availableUnits, quantityUnit]);

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError(null);
    // if quantityError is set, we should not submit the form
    // and should refocus on the quantity input
    if (quantityError) {
      qtyInputRef.current?.focus();
      return;
    }
    if (!quantityRequired) {
      setError("Please enter a quantity");
      qtyInputRef.current?.focus();
      return;
    }

    if (showAddressInput || !isAddressProvided(deliveryAddress)) {
      setError("Please provide a delivery address.");
      return;
    }

    const trimmedGuestEmail = guestEmail.trim();
    if (!user && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedGuestEmail)) {
      setError("Please enter a valid email address.");
      return;
    }

    const cart = parseStoredCart(localStorage.getItem("shoppingCart"));
    let cartItems = cart.cartItems as CartItem[];
    const productInCart = cartItems.some(
      (item) => item.product.id === product.id,
    );

    console.log("Cart items:", cartItems);
    console.log("Product in cart:", productInCart);

    if (productInCart) {
      // remove the product from the cart so that we can add it again with the new details
      cartItems = cartItems.filter((item) => item.product.id !== product.id);
    }

    if (product.price?.unit && !quantityUnit) {
      setError("Please select a quantity unit.");
      return;
    }

    if (!user) {
      localStorage.setItem(LAST_USED_GUEST_EMAIL_KEY, trimmedGuestEmail);
      localStorage.setItem(LAST_USED_GUEST_PHONE_KEY, guestPhone.trim());
      setHasCachedGuestEmail(true);
      identifyAnalyticsUser(posthog, trimmedGuestEmail);
    }

    cartItems.push({
      product,
      quantityRequired: {
        value: parseFloat(quantityRequired) || 1, // Default to 1 if not specified
        unit: quantityUnit || product.price?.unit!,
      },
      deliveryAddress,
      millCertRequired,
      notes,
    });

    persistShoppingCart({ cartItems });
    console.log("Updated cart items:", cartItems);
    captureAnalyticsEvent(posthog, "product_added_to_cart", {
      ...productInterestProps(product),
      productId: product.id,
      quantityRequired: parseFloat(quantityRequired) || 1,
      quantityUnit: quantityUnit || product.price?.unit,
    });
    if (searchQuery) {
      pingShoppingCartUpdateNotification();
      navigate(`/?q=${encodeURIComponent(searchQuery)}`);
    } else {
      // the user probably went to the listing directly via a link,
      // so we navigate them to the cart page after adding the item
      navigate("/cart");
    }
  };
  const showComputedPrice = () => {
    if (!computedPrice) {
      return "Price available upon request";
    }
    if (!quantityRequired || quantityRequired === "") {
      return "Enter quantity to see price";
    }
    // Length orders on products with vendor cut tiers price as the cheapest
    // combination of cuts (a 3 ft order uses the 36" cut price, not 3 x the
    // 12" cut). Matches the backend's authoritative computeLineAmount.
    const tieredTotal = cheapestTierTotal(
      product.lengthPriceTiers,
      parseFloat(quantityRequired) || 0,
      quantityUnit || "",
    );
    return (
      <>
        Order total:{" "}
        <span className="font-medium">
          {formatPriceValueString({
            value:
              tieredTotal ??
              computedPrice.value * (parseFloat(quantityRequired) || 0),
            currency: product.price?.currency || "USD",
          })}
        </span>
      </>
    );
  };

  const quantityEntry = () => {
    return (
      <div className="relative px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]">
        <div className="flex">
          <label
            htmlFor="input-quantity"
            className="block text-xs font-medium text-gray-900"
          >
            Quantity Required
          </label>
          {quantityError && (
            <span className="text-xs font-medium text-red-500 ml-2">
              ! {quantityError}
            </span>
          )}
        </div>
        <input
          id="input-quantity"
          name="quantity"
          type="number"
          ref={qtyInputRef}
          placeholder="Enter quantity"
          value={quantityRequired}
          onChange={saveInputChangeInHookState(setQuantityRequired)}
          className="border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
        />
        <UnitSelect
          options={availableUnits.map((unit) => ({
            value: unit,
            label: simpleFormatUnit(unit),
          }))}
          onChange={(value) => setQuantityUnit(value)}
          value={quantityUnit}
        />
      </div>
    );
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        className="rounded-full bg-[#00062e] hover:bg-slate-900 focus:bg-slate-900 text-white font-bold block w-full mt-3 py-2"
      >
        Add to Cart
      </button>
    );
  }

  return (
    <div>
      <div className="flex items-center mt-3 mb-3">
        <div className="flex-grow border-t border-gray-300 mt-2"></div>
        <span
          className="px-4 font-bold text-gray-900 pt-2 cursor-pointer"
          onClick={() => setOpen(false)}
        >
          Add to Cart
        </span>
        <div className="flex-grow border-t border-gray-300 mt-2"></div>
      </div>
      <div className="animate-expand-down overflow-hidden">
        <form onSubmit={handleSubmit}>
          <div className="isolate -space-y-px rounded-xl shadow-sm mt-3">
            <div
              className={classNames(
                "relative px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e] rounded-xl rounded-b-none",
              )}
            >
              <label
                htmlFor="input-location"
                className="block text-xs font-medium text-gray-900"
              >
                Delivery Address
              </label>
              {showAddressInput ? (
                <AddressAutocomplete
                  onAddressSelected={handleAddressSelected}
                  placeholder="Enter delivery address"
                  inputClassName="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  dropdownClassName="absolute z-10 bg-white border border-gray-300 w-full mt-1 rounded shadow-lg"
                />
              ) : (
                <div className="flex items-center justify-between">
                  <span className="block text-sm text-gray-900">
                    {deliveryAddress &&
                      formatAddress(deliveryAddress, AddressFormat.Full)}
                  </span>
                  <button
                    type="button"
                    onClick={() => setShowAddressInput(true)}
                    className="text-xs text-blue-600 hover:text-blue-800 ml-2"
                  >
                    Edit
                  </button>
                </div>
              )}
            </div>
            {!user && !hasCachedGuestEmail && (
              <div className="grid grid-cols-1 lg:grid-cols-2 lg:-space-x-px">
                <div className="relative px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]">
                  <label
                    htmlFor="input-email"
                    className="block text-xs font-medium text-gray-900"
                  >
                    Email
                  </label>
                  <input
                    id="input-email"
                    name="email"
                    type="email"
                    placeholder="john@example.com"
                    value={guestEmail}
                    onChange={saveInputChangeInHookState(setGuestEmail)}
                    required
                    className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
                <div className="relative hidden px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e] lg:block">
                  <label
                    htmlFor="input-phone"
                    className="block text-xs font-medium text-gray-900"
                  >
                    Phone Number (optional)
                  </label>
                  <input
                    id="input-phone"
                    name="phone"
                    type="tel"
                    placeholder="(555) 123-4567"
                    value={guestPhone}
                    onChange={saveInputChangeInHookState(setGuestPhone)}
                    className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
                  />
                </div>
              </div>
            )}
            {quantityEntry()}
            <div className="relative px-3 pb-4 pt-4 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]">
              <div className="relative flex items-start">
                <div className="flex h-6 items-center">
                  <input
                    id="input-certificate"
                    name="certificate"
                    type="checkbox"
                    checked={millCertRequired}
                    onChange={(e) => setMillCertRequired(e.target.checked)}
                    className="h-4 w-4 rounded border-gray-300 text-[#00062e] focus:ring-[#00062e]"
                  />
                </div>
                <div className="ml-3 text-xs leading-6">
                  <label
                    htmlFor="input-certificate"
                    className="font-medium text-gray-900"
                  >
                    Mill Certificate Required
                  </label>
                </div>
              </div>{" "}
            </div>
            <div className="relative rounded-xl rounded-t-none px-3 pb-1.5 pt-2.5 ring-1 ring-inset ring-gray-300 focus-within:z-10 focus-within:ring-2 focus-within:ring-[#00062e]">
              <label
                htmlFor="input-notes"
                className="block text-xs font-medium text-gray-900"
              >
                Notes
              </label>
              <input
                id="input-notes"
                name="notes"
                type="text"
                placeholder="..."
                value={notes}
                onChange={saveInputChangeInHookState(setNotes)}
                className="block w-full border-0 p-0 text-gray-900 placeholder:text-gray-400 focus:ring-0 sm:text-sm sm:leading-6"
              />
            </div>
          </div>
          {error && <div className="text-xs text-red-500">{error}</div>}
          <div className="text-sm text-gray-500 mt-2 text-right">
            {showComputedPrice()}
          </div>
          <button
            type="submit"
            className="rounded-full bg-[#00062e] hover:bg-slate-900 focus:bg-slate-900 text-white font-bold block w-full mt-3 py-2"
          >
            Add to Cart
          </button>
        </form>
      </div>
    </div>
  );
};
